import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  Button,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Divider,
  Checkbox,
  Link,
  Stack,
} from '@mui/material';
import { Close, Email, Phone } from '@mui/icons-material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { CompactButton } from './CompactButton.styles';
import { LockableSelect } from './LockableSelect.styles';
import { AlertEditFormData, Alert } from '../../types';
import { fieldTopGapSx } from '../../helpers/sx';
import { selectProfile } from 'src/modules/profile/slices';
import { Application } from '../../../applications/types/Application';
import { getApplication } from '../../../applications/slices/applicationsSlice';
import { getCurrentCompanies, getCurrentCompanyId } from 'src/modules/companies/slices';
import { useUserCompaniesService } from 'src/modules/companies/services';

interface AlertDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AlertEditFormData | Alert) => void;
  alert?: Alert | null; // If provided, we're editing; if null/undefined, we're adding
  loading?: boolean;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const convertTimeStringToDateTime = (time: string) => {
  if (time) {
    return new Date('1970-01-01T' + time).toUTCString();
  }
  return '';
};

const isAllDayAlert = (startTime: string, endTime: string): boolean => {
  // Handle various time formats that represent "All day"
  const allDayPatterns = [
    // 24-hour format: 00:00 to 23:59
    { start: '00:00', end: '23:59' },
    // 24-hour format with seconds: 00:00:00 to 23:59:00
    { start: '00:00:00', end: '23:59:00' },
    // 12-hour format: 12:00 AM to 11:59 PM
    { start: '12:00 AM', end: '11:59 PM' },
    // Alternative formats
    { start: '12:00:00 AM', end: '11:59:59 PM' },
  ];

  return allDayPatterns.some(
    pattern =>
      startTime.toLowerCase() === pattern.start.toLowerCase() &&
      endTime.toLowerCase() === pattern.end.toLowerCase(),
  );
};

const getIntervalLimits = (period: string) => {
  switch (period) {
    case 'minute':
      return { min: 1, max: 60, unit: 'minute(s)' };
    case 'hour':
      return { min: 1, max: 24, unit: 'hour(s)' };
    case 'day':
      return { min: 1, max: 365, unit: 'day(s)' };
    default:
      return { min: 1, max: 365, unit: 'unit(s)' };
  }
};

const getFrequencyFromOption = (option: string) => {
  switch (option) {
    case '15min':
      return { period: 'minute', interval: 15 };
    case 'hourly':
      return { period: 'hour', interval: 1 };
    case 'daily':
      return { period: 'day', interval: 1 };
    case 'custom':
      return null;
    default:
      return null;
  }
};

const initialFormData: Omit<AlertEditFormData, 'companyId'> = {
  mode: 'on_new_messages',
  processMessages: ['all'],
  period: 'minute',
  interval: 15,
  onlyIfUnreadMessages: false,
  startTime: '00:00',
  endTime: '23:59',
  daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
  destinationEmail: null,
  destinationPhoneNumber: null,
  deliveryMethod: 'email',
};

export const AlertDrawer: React.FC<AlertDrawerProps> = ({
  open,
  onClose,
  onSave,
  alert,
  loading = false,
}) => {
  const [formData, setFormData] = useState<AlertEditFormData>({
    ...initialFormData,
    companyId: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof AlertEditFormData, string>>>({});
  const [companySelectOpen, setCompanySelectOpen] = useState(false);
  const [frequencyOption, setFrequencyOption] = useState<'15min' | 'hourly' | 'daily' | 'custom'>(
    '15min',
  );
  const [activeDays, setActiveDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [alertHours, setAlertHours] = useState<{ startTime: string; endTime: string }>({
    startTime: '09:00',
    endTime: '17:00',
  });
  const [isAllDay, setIsAllDay] = useState<boolean>(false);
  const [originalAlertHours, setOriginalAlertHours] = useState<{
    startTime: string;
    endTime: string;
  }>({ startTime: '09:00', endTime: '17:00' });
  const application = useSelector(getApplication) as Application;
  const navigate = useNavigate();

  // Company-related data
  const companies = useSelector(getCurrentCompanies);
  const currentCompanyId = useSelector(getCurrentCompanyId);
  const { getCompanies } = useUserCompaniesService();

  // Get current user profile for contact information
  const profileState = useSelector(selectProfile);
  const profile = profileState?.profile;
  const isEditing = !!alert;

  // Check if user has phone number
  const hasPhoneNumber = !!profile?.phoneNumber;

  // Company options for dropdown
  const companyOptions = useMemo(() => {
    return companies.map(company => ({
      value: company.id,
      title: company.name,
    }));
  }, [companies]);

  // Check if there's only one company (to modify behavior like in header)
  const hasOnlyOneCompany = companies.length === 1;

  // Initialize/populate form data
  useEffect(() => {
    if (alert) {
      // Editing mode - populate with alert data
      const deliveryMethod =
        alert.destinationEmail && alert.destinationPhoneNumber
          ? 'both'
          : alert.destinationEmail
          ? 'email'
          : 'sms';

      setFormData({
        companyId: alert.company.id,
        mode: alert.mode,
        processMessages: alert.processMessages,
        period: alert.period,
        interval: alert.interval,
        onlyIfUnreadMessages: alert.onlyIfUnreadMessages,
        startTime: alert.startTime,
        endTime: alert.endTime,
        daysOfWeek: alert.daysOfWeek,
        destinationEmail: alert.destinationEmail,
        destinationPhoneNumber: alert.destinationPhoneNumber,
        deliveryMethod: deliveryMethod as AlertEditFormData['deliveryMethod'],
      });

      // Determine frequency option from existing data
      if (alert.period === 'minute' && alert.interval === 15) {
        setFrequencyOption('15min');
      } else if (alert.period === 'hour' && alert.interval === 1) {
        setFrequencyOption('hourly');
      } else if (alert.period === 'day' && alert.interval === 1) {
        setFrequencyOption('daily');
      } else {
        setFrequencyOption('custom');
      }

      // Set active days and alert hours from existing data
      setActiveDays(alert.daysOfWeek);
      setAlertHours({ startTime: alert.startTime, endTime: alert.endTime });
      setOriginalAlertHours({ startTime: alert.startTime, endTime: alert.endTime });
      // Check if this is an "All day" alert
      setIsAllDay(isAllDayAlert(alert.startTime, alert.endTime));

      setErrors({});
    } else if (open) {
      // Adding mode - reset to initial data when drawer opens with current company
      setFormData({ ...initialFormData, companyId: currentCompanyId || '' });
      setFrequencyOption('15min');
      setActiveDays([0, 1, 2, 3, 4, 5, 6]);
      setAlertHours({ startTime: '09:00', endTime: '17:00' });
      setIsAllDay(false);
      setErrors({});
    }

    // Close company select when drawer closes
    if (!open) {
      setCompanySelectOpen(false);
    }
  }, [alert, open, currentCompanyId]);

  // Load companies when drawer opens
  useEffect(() => {
    if (open && companies.length === 0) {
      getCompanies();
    }
  }, [open, companies.length, getCompanies]);

  // Adjust interval when period changes to ensure it's within valid range
  useEffect(() => {
    const limits = getIntervalLimits(formData.period);
    // Only auto-adjust if the field is not empty (interval !== 0) and out of range
    if (
      formData.interval !== 0 &&
      (formData.interval < limits.min || formData.interval > limits.max)
    ) {
      const adjustedInterval = Math.min(Math.max(formData.interval, limits.min), limits.max);
      setFormData(prev => ({ ...prev, interval: adjustedInterval }));

      // Clear interval errors since we're auto-adjusting
      if (errors.interval) {
        setErrors(prev => ({ ...prev, interval: undefined }));
      }
    }
  }, [formData.period, formData.interval, errors.interval]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AlertEditFormData, string>> = {};

    // Validate company selection
    if (!formData.companyId) {
      newErrors.companyId = 'Please select a company';
    }

    // Validate interval based on period
    const limits = getIntervalLimits(formData.period);
    if (formData.interval === 0) {
      newErrors.interval = 'Interval is required';
    } else if (formData.interval < limits.min || formData.interval > limits.max) {
      newErrors.interval = `Interval must be between ${limits.min} and ${limits.max} ${limits.unit}`;
    }

    if (activeDays.length === 0) {
      newErrors.daysOfWeek = 'Please select at least one day';
    }

    if (activeDays.some(day => day < 0 || day > 6 || !Number.isInteger(day))) {
      newErrors.daysOfWeek = 'Invalid day selected';
    }

    if (!formData.processMessages || formData.processMessages.length === 0) {
      newErrors.processMessages = 'Please select a message filter';
    }

    // Validate time fields - always required
    if (!alertHours.startTime) {
      newErrors.startTime = 'Start Time is required';
    }

    if (!alertHours.endTime) {
      newErrors.endTime = 'End Time is required';
    }

    // Validate time range when both times are provided
    if (alertHours.startTime && alertHours.endTime) {
      const startTime = dayjs(`1970-01-01T${alertHours.startTime}`);
      const endTime = dayjs(`1970-01-01T${alertHours.endTime}`);

      if (endTime.isBefore(startTime) || endTime.isSame(startTime)) {
        newErrors.endTime = 'End time must be later than start time';
      }
    }

    // Note: For adding new alerts, contact information validation is handled by the backend
    // using the user's registered contact details

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      if (isEditing && alert) {
        // Editing mode - return updated Alert object
        const updatedAlert: Alert = {
          ...alert,
          mode: formData.mode,
          processMessages: formData.processMessages,
          period: formData.period,
          interval: formData.interval,
          onlyIfUnreadMessages: formData.onlyIfUnreadMessages,
          startTime: alertHours.startTime,
          endTime: alertHours.endTime,
          daysOfWeek: activeDays,
          // Update delivery method using logged user's contact info
          destinationEmail:
            formData.deliveryMethod === 'email' || formData.deliveryMethod === 'both'
              ? profile?.email || null
              : null,
          destinationPhoneNumber:
            formData.deliveryMethod === 'sms' || formData.deliveryMethod === 'both'
              ? profile?.phoneNumber || null
              : null,
        };
        onSave(updatedAlert);
      } else {
        // Adding mode - return form data with logged user's contact info
        const alertData = {
          ...formData,
          startTime: alertHours.startTime,
          endTime: alertHours.endTime,
          daysOfWeek: activeDays,
          // Use logged user's contact information based on delivery method
          destinationEmail:
            formData.deliveryMethod === 'email' || formData.deliveryMethod === 'both'
              ? profile?.email || null
              : null,
          destinationPhoneNumber:
            formData.deliveryMethod === 'sms' || formData.deliveryMethod === 'both'
              ? profile?.phoneNumber || null
              : null,
        };
        onSave(alertData);
      }
    }
  };

  const handleStartTimeChange = (value: dayjs.Dayjs | null) => {
    const timeString = value?.format('HH:mm') || '';
    setAlertHours(prev => ({
      ...prev,
      startTime: timeString,
    }));

    if (errors.startTime) {
      setErrors(prev => ({ ...prev, startTime: undefined }));
    }
  };

  const handleEndTimeChange = (value: dayjs.Dayjs | null) => {
    const timeString = value?.format('HH:mm') || '';
    setAlertHours(prev => ({
      ...prev,
      endTime: timeString,
    }));

    if (errors.endTime) {
      setErrors(prev => ({ ...prev, endTime: undefined }));
    }
  };

  const handleAllDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsAllDay(checked);

    if (checked) {
      // Store current times before setting to all day
      setOriginalAlertHours({ startTime: alertHours.startTime, endTime: alertHours.endTime });
      setAlertHours({ startTime: '00:00', endTime: '23:59' });
    } else {
      // Restore original times when unchecking all day
      setAlertHours(originalAlertHours);
    }

    if (errors.startTime || errors.endTime) {
      setErrors(prev => ({ ...prev, startTime: undefined, endTime: undefined }));
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack width={400} p={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">{isEditing ? 'Edit Alert' : 'Add New Alert'}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>

        <Stack gap={2}>
          {/* Configuration Header */}
          <Typography variant="h6" color="text.primary" fontWeight="fontWeightSemiBold">
            Configuration
          </Typography>

          {/* Company Selection */}
          <FormControl fullWidth error={!!errors.companyId}>
            <Typography variant="subtitle1" color="text.secondary">
              Company:
            </Typography>
            <LockableSelect
              size="small"
              value={formData.companyId || ''}
              open={hasOnlyOneCompany || isEditing ? false : companySelectOpen}
              onClose={() => {
                if (!hasOnlyOneCompany && !isEditing) {
                  setCompanySelectOpen(false);
                }
              }}
              onOpen={() => {
                if (!hasOnlyOneCompany && !isEditing) {
                  setCompanySelectOpen(true);
                }
              }}
              onClick={() => {
                if (hasOnlyOneCompany || isEditing) return; // Don't allow changes if only one company or editing
                setCompanySelectOpen(!companySelectOpen);
              }}
              disabled={hasOnlyOneCompany || isEditing}
              onChange={e => {
                if (hasOnlyOneCompany || isEditing) return; // Don't allow changes if only one company or editing

                const value = e.target.value as string;
                setFormData(prev => ({ ...prev, companyId: value }));
                // Clear company error when selection is made
                if (errors.companyId) {
                  setErrors(prev => ({ ...prev, companyId: undefined }));
                }
                setCompanySelectOpen(false);
              }}
              locked={hasOnlyOneCompany || isEditing}
              sx={fieldTopGapSx}
            >
              {companyOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.title}
                </MenuItem>
              ))}
            </LockableSelect>
          </FormControl>

          {/* Alert Mode */}
          <FormControl fullWidth>
            <Typography variant="subtitle1" color="text.secondary">
              Alerts For:
            </Typography>
            <Select
              size="small"
              value={formData.mode}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  mode: e.target.value as AlertEditFormData['mode'],
                }))
              }
              sx={fieldTopGapSx}
            >
              <MenuItem value="on_new_messages">Messages Outside of Campaigns</MenuItem>
              <MenuItem value="always">All Inbound Messages</MenuItem>
            </Select>
          </FormControl>

          {/* Message Processing - Dropdown */}
          <FormControl fullWidth error={!!errors.processMessages}>
            <Typography variant="subtitle1" color="text.secondary">
              From:
            </Typography>
            <Select
              size="small"
              value={
                formData.processMessages?.length === 2 &&
                formData.processMessages.includes('not_assigned') &&
                formData.processMessages.includes('assigned_to_user')
                  ? 'assigned_to_me_or_unassigned'
                  : formData.processMessages?.[0] || 'all'
              }
              onChange={e => {
                const value = e.target.value;
                if (value === 'assigned_to_me_or_unassigned') {
                  setFormData(prev => ({
                    ...prev,
                    processMessages: ['not_assigned', 'assigned_to_user'],
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    processMessages: [value as 'all' | 'assigned_to_user' | 'not_assigned'],
                  }));
                }
              }}
              sx={fieldTopGapSx}
            >
              <MenuItem value="all">All Contacts</MenuItem>
              <MenuItem value="assigned_to_user">Contacts Assigned to Me</MenuItem>
              <MenuItem value="assigned_to_me_or_unassigned">
                Contacts Assigned to Me or Unassigned
              </MenuItem>
            </Select>
            {errors.processMessages && (
              <Typography variant="caption" color="error" mt={1}>
                {errors.processMessages}
              </Typography>
            )}
          </FormControl>

          <Divider />

          <Typography variant="h6" color="text.primary" mb={0} fontWeight="fontWeightSemiBold">
            Schedule
          </Typography>

          {/* Frequency Subsection */}
          <Box>
            <Typography variant="subtitle1" mb={1} color="text.secondary">
              Frequency:
            </Typography>
            <FormControl fullWidth>
              <Select
                size="small"
                value={frequencyOption}
                onChange={e => {
                  const option = e.target.value as typeof frequencyOption;
                  setFrequencyOption(option);

                  const frequency = getFrequencyFromOption(option);
                  if (frequency) {
                    setFormData(prev => ({
                      ...prev,
                      period: frequency.period as AlertEditFormData['period'],
                      interval: frequency.interval,
                    }));
                  }
                }}
              >
                <MenuItem value="15min">Every 15 Minutes</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>

            {/* Custom frequency controls */}
            {frequencyOption === 'custom' && (
              <Stack direction="row" gap={1.5} mt={1.5}>
                <TextField
                  label="Every"
                  value={formData.interval === 0 ? '' : formData.interval.toString()}
                  onChange={e => {
                    const inputValue = e.target.value;

                    // Allow clearing the field
                    if (inputValue === '') {
                      setFormData(prev => ({ ...prev, interval: 0 }));
                      return;
                    }

                    // Only allow numeric input
                    const numericValue = inputValue.replace(/[^0-9]/g, '');
                    if (numericValue !== inputValue) {
                      return; // Don't update if non-numeric characters were entered
                    }

                    const value = parseInt(numericValue) || 0;
                    const limits = getIntervalLimits(formData.period);
                    const clampedValue = Math.min(Math.max(value, limits.min), limits.max);
                    setFormData(prev => ({ ...prev, interval: clampedValue }));
                  }}
                  sx={{ flex: 1 }}
                  error={!!errors.interval}
                  helperText={
                    errors.interval ||
                    `Range: ${getIntervalLimits(formData.period).min}-${
                      getIntervalLimits(formData.period).max
                    }`
                  }
                  placeholder="Enter number"
                />
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Period</InputLabel>
                  <Select
                    size="small"
                    value={formData.period}
                    label="Period"
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        period: e.target.value as AlertEditFormData['period'],
                      }));
                      // Clear interval and time errors when period changes since validation rules change
                      if (errors.interval || errors.startTime || errors.endTime) {
                        setErrors(prev => ({
                          ...prev,
                          interval: undefined,
                          startTime: undefined,
                          endTime: undefined,
                        }));
                      }
                    }}
                  >
                    <MenuItem value="minute">Minute(s)</MenuItem>
                    <MenuItem value="hour">Hour(s)</MenuItem>
                    <MenuItem value="day">Day(s)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}
          </Box>

          {/* Trigger Subsection */}
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Trigger:
            </Typography>
            <FormControl fullWidth>
              <Select
                size="small"
                value={formData.onlyIfUnreadMessages ? 'onlyIfUnreadMessages' : 'always'}
                onChange={e => {
                  setFormData(prev => ({
                    ...prev,
                    onlyIfUnreadMessages: e.target.value === 'onlyIfUnreadMessages',
                  }));
                }}
                sx={fieldTopGapSx}
              >
                <MenuItem value="always">Always</MenuItem>
                <MenuItem value="onlyIfUnreadMessages">If Unread Messages Exist</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Alert Days Subsection */}
          <Box>
            <Typography variant="subtitle1" mb={1} color="text.secondary">
              Alert Days:
            </Typography>

            {/* Helper buttons */}
            <Stack direction="row" gap={1} mb={1}>
              <Button
                variant={activeDays.length === 7 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setActiveDays([0, 1, 2, 3, 4, 5, 6])}
              >
                All Days
              </Button>
              <Button
                variant={
                  activeDays.length === 5 && activeDays.includes(1) && activeDays.includes(5)
                    ? 'contained'
                    : 'outlined'
                }
                size="small"
                onClick={() => setActiveDays([1, 2, 3, 4, 5])}
              >
                Weekdays
              </Button>
              <Button
                variant={
                  activeDays.length === 2 && activeDays.includes(0) && activeDays.includes(6)
                    ? 'contained'
                    : 'outlined'
                }
                size="small"
                onClick={() => setActiveDays([0, 6])}
              >
                Weekend
              </Button>
            </Stack>

            {/* Day selection */}
            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
              {DAYS_OF_WEEK.map(day => (
                <CompactButton
                  key={day.value}
                  variant={activeDays.includes(day.value) ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    if (activeDays.includes(day.value)) {
                      setActiveDays(prev => prev.filter(d => d !== day.value));
                    } else {
                      setActiveDays(prev => [...prev, day.value].sort());
                    }
                  }}
                >
                  {day.label.substring(0, 3)}
                </CompactButton>
              ))}
            </Box>
            {errors.daysOfWeek && (
              <Typography color="error" variant="caption" mt={1} display="block">
                {errors.daysOfWeek}
              </Typography>
            )}
          </Box>

          {/* Alert Hours Subsection */}
          <Box>
            <Typography variant="subtitle1" mb={0} color="text.secondary">
              Alert Hours:
            </Typography>

            {/* All day checkbox */}
            <FormControlLabel
              control={<Checkbox checked={isAllDay} onChange={handleAllDayChange} size="small" />}
              label="All day"
              sx={{ mb: 2 }}
            />

            <Stack direction="row" gap={1.5}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  label="Start Time"
                  value={
                    alertHours.startTime
                      ? dayjs(convertTimeStringToDateTime(alertHours.startTime))
                      : null
                  }
                  ampm
                  format="hh:mm A"
                  onChange={handleStartTimeChange}
                  disabled={isAllDay}
                  slotProps={{
                    textField: {
                      sx: { flex: 1 },
                      error: !!errors.startTime,
                      helperText: errors.startTime,
                    },
                    popper: {
                      placement: 'bottom-end',
                    },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  label="End Time"
                  value={
                    alertHours.endTime
                      ? dayjs(convertTimeStringToDateTime(alertHours.endTime))
                      : null
                  }
                  ampm
                  format="hh:mm A"
                  onChange={handleEndTimeChange}
                  disabled={isAllDay}
                  minTime={
                    alertHours.startTime
                      ? dayjs(convertTimeStringToDateTime(alertHours.startTime))
                      : undefined
                  }
                  slotProps={{
                    textField: {
                      sx: { flex: 1 },
                      error: !!errors.endTime,
                      helperText: errors.endTime,
                    },
                    popper: {
                      placement: 'bottom-end',
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>
          </Box>

          <Divider />

          {/* Delivery Method */}
          <Box mb={2}>
            <Typography variant="h6" color="text.primary" fontWeight="fontWeightSemiBold">
              Delivery Method:
            </Typography>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <RadioGroup
                value={formData.deliveryMethod}
                onChange={e => {
                  const newDeliveryMethod = e.target.value as AlertEditFormData['deliveryMethod'];

                  // If user tries to select SMS options without phone number, default to email
                  if (
                    (newDeliveryMethod === 'sms' || newDeliveryMethod === 'both') &&
                    !hasPhoneNumber
                  ) {
                    setFormData(prev => ({ ...prev, deliveryMethod: 'email' }));
                  } else {
                    setFormData(prev => ({ ...prev, deliveryMethod: newDeliveryMethod }));
                  }
                }}
              >
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label={
                    <>
                      Email <em>({application.priceForAlert} Credit per Alert)</em>
                    </>
                  }
                />
                <FormControlLabel
                  value="sms"
                  control={<Radio />}
                  label={
                    <>
                      Text <em>({application.priceForAlert} Credit per Alert)</em>
                    </>
                  }
                  disabled={!hasPhoneNumber}
                />
                <FormControlLabel
                  value="both"
                  control={<Radio />}
                  label={
                    <>
                      Both Email and Text{' '}
                      <em>({application.priceForAlert * 2} Credits per Alert)</em>
                    </>
                  }
                  disabled={!hasPhoneNumber}
                />
              </RadioGroup>
            </FormControl>

            {/* Show warning if no phone number */}
            {!hasPhoneNumber &&
              (formData.deliveryMethod === 'email' || formData.deliveryMethod === 'both') && (
                <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                  <Link
                    component="button"
                    variant="body2"
                    color="primary.main"
                    underline="always"
                    onClick={() => {
                      onClose();
                      navigate('/profile');
                    }}
                  >
                    Add your phone number to enable text alerts
                  </Link>
                </Box>
              )}

            {/* Show contact information */}
            <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
              <Stack gap={1}>
                {(formData.deliveryMethod === 'email' || formData.deliveryMethod === 'both') &&
                  profile?.email && (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Email fontSize="small" color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        {profile.email}
                      </Typography>
                    </Stack>
                  )}
                {(formData.deliveryMethod === 'sms' || formData.deliveryMethod === 'both') &&
                  profile?.phoneNumber && (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Phone fontSize="small" color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        {profile.phoneNumber}
                      </Typography>
                    </Stack>
                  )}
              </Stack>
            </Box>
          </Box>
        </Stack>

        <Stack direction="row" gap={2} justifyContent="flex-end" mt={3}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? (isEditing ? 'Saving...' : 'Adding...') : isEditing ? 'Update' : 'Add Alert'}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};
